import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Users,
  Activity,
  DollarSign,
  AlertTriangle,
  Hourglass,
  LogOut,
  Search,
  Loader2,
  Power,
  Check,
  Pencil,
  X,
  Link as LinkIcon,
  Copy,
  History,
  RefreshCw,
  Clock,
  UserPlus,
  Wand2,
  Mail,
  Trash2,
} from "lucide-react";
import { api, authHeaders, setAdminToken, getAdminToken } from "../lib/api";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const STATUS_META = {
  trial:          { label: "Trial",          cls: "pill-active" },
  active:         { label: "Active",         cls: "pill-active" },
  payment_failed: { label: "Payment Failed", cls: "pill-failed" },
  inactive:       { label: "Inactive",       cls: "pill-inactive" },
  pending:        { label: "Pending",        cls: "pill-pending" },
};

const INVITE_META = {
  not_invited: { label: "Not invited", cls: "pill-inactive" },
  invited:     { label: "Invite sent", cls: "pill-active" },
  expired:     { label: "Expired",     cls: "pill-failed" },
  signed_up:   { label: "Signed up",   cls: "pill-active" },
};

// Per-invite history record states (richer than INVITE_META above).
const INVITE_HISTORY_META = {
  pending:    { label: "Pending",    cls: "pill-pending" },
  clicked:    { label: "Clicked",    cls: "pill-active"  },
  signed_up:  { label: "Signed up",  cls: "pill-active"  },
  expired:    { label: "Expired",    cls: "pill-failed"  },
  revoked:    { label: "Replaced",   cls: "pill-inactive"},
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"numeric" });
  } catch { return iso; }
}

/** Convert an ISO datetime (or plain YYYY-MM-DD) into the `YYYY-MM-DD` form
 *  required by `<input type="date">`. Returns `""` for null/invalid. */
function toDateInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ""; }
}

/** Native `<input type="date">` inline editor for a client's trial_end_date.
 *  Click the date to enter edit mode → confirm/cancel buttons appear. */
function EditableTrialEndCell({ clientId, value, busy, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(toDateInputValue(value));
  const todayStr = new Date().toISOString().slice(0, 10);
  // "Tomorrow" is the minimum acceptable date because the backend rejects any
  // trial_end_date that isn't strictly in the future.
  const minStr = todayStr;

  React.useEffect(() => {
    if (!editing) setDraft(toDateInputValue(value));
  }, [value, editing]);

  const cancel = () => {
    setEditing(false);
    setDraft(toDateInputValue(value));
  };

  const confirm = async () => {
    if (!draft) return;
    // Send as YYYY-MM-DD → backend folds to UTC midnight.
    const ok = await onSave(clientId, draft);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`trial-end-editor-${clientId}`}>
        <input
          type="date"
          value={draft}
          min={minStr}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          data-testid={`trial-end-input-${clientId}`}
          className="bg-[#091324] border border-white/10 rounded px-2 py-1 text-xs font-mono text-[#F8FAFC] focus:outline-none focus:border-[#06B6D4] disabled:opacity-50"
        />
        <button
          onClick={confirm}
          disabled={busy || !draft}
          data-testid={`trial-end-confirm-${clientId}`}
          aria-label="Save trial end date"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        </button>
        <button
          onClick={cancel}
          disabled={busy}
          data-testid={`trial-end-cancel-${clientId}`}
          aria-label="Cancel"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/10 text-[#94A3B8] hover:bg-white/5 disabled:opacity-50"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`trial-end-display-${clientId}`}
      aria-label="Edit trial end date"
      title="Click to edit trial end date"
      className="text-sm font-mono text-[#F8FAFC] hover:text-[#06B6D4] transition-colors inline-flex items-center gap-1.5 group"
    >
      <span>{fmtDate(value)}</span>
      <Pencil size={11} className="text-[#94A3B8] group-hover:text-[#06B6D4] opacity-70 group-hover:opacity-100" />
    </button>
  );
}

function StatCard({ icon: Icon, label, value, accent, tone = "cyan" }) {
  const toneCls = tone === "pink" ? "text-[#EC4899]" : "text-[#06B6D4]";
  return (
    <div className="so-card rounded-xl p-5">
      <div className="flex items-center justify-between">
        <div className="uppercase text-[11px] tracking-widest text-[#94A3B8] font-mono">
          {label}
        </div>
        <Icon size={16} className={toneCls} />
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight">{value}</div>
      {accent && <div className="mt-2 text-xs text-[#94A3B8] font-mono">{accent}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.inactive;
  return <span className={`pill ${m.cls}`} data-testid={`status-${status}`}>● {m.label}</span>;
}

function InviteCell({ clientId, invite_status, busy, onGenerate, onViewHistory, lastInvite }) {
  const m = INVITE_META[invite_status] || INVITE_META.not_invited;
  const canGenerate = invite_status === "not_invited" || invite_status === "expired";
  const canRegenerate = invite_status === "invited"; // allow refreshing if old link is lost
  const isFinal = invite_status === "signed_up";
  const hasHistory = invite_status !== "not_invited";
  const [justCopied, setJustCopied] = useState(false);

  const copyLink = async () => {
    if (!lastInvite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(lastInvite.invite_url);
      setJustCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setJustCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <div className="flex items-center gap-1.5 min-w-0 flex-wrap max-w-full">
      <span
        data-testid={`invite-status-${clientId}`}
        className={`pill ${m.cls} shrink-0`}
      >
        ● {m.label}
      </span>
      {lastInvite?.invite_url && !isFinal && (
        <button
          type="button"
          onClick={copyLink}
          data-testid={`invite-copy-${clientId}`}
          title={lastInvite.invite_url}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-[#94A3B8] hover:text-[#06B6D4] hover:border-[#06B6D4]/40"
          aria-label="Copy invite link"
        >
          {justCopied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      )}
      {(canGenerate || canRegenerate) && !isFinal && (
        <button
          type="button"
          onClick={() => onGenerate(clientId)}
          disabled={busy}
          data-testid={`invite-generate-${clientId}`}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 px-2 py-1 text-[11px] font-mono uppercase tracking-wider disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <LinkIcon size={11} />
          )}
          {canRegenerate ? "New link" : "Generate"}
        </button>
      )}
      {hasHistory && (
        <button
          type="button"
          onClick={() => onViewHistory(clientId)}
          data-testid={`invite-history-${clientId}`}
          title="View invite history"
          aria-label="View invite history"
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-[#94A3B8] hover:text-[#06B6D4] hover:border-[#06B6D4]/40"
        >
          <History size={12} />
        </button>
      )}
    </div>
  );
}

function WidgetIdCell({ clientId, current, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = () => {
    setDraft(current || "");
    setEditing(true);
  };

  const commit = async () => {
    if (draft === (current || "")) {
      setEditing(false);
      return;
    }
    const ok = await onSave(clientId, draft);
    if (ok) setEditing(false);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") cancel();
          }}
          placeholder="e.g. sparkleclean-co"
          data-testid={`widget-id-input-${clientId}`}
          className="so-input flex-1 min-w-0 rounded-md px-2.5 py-1.5 text-xs font-mono"
        />
        <button
          type="button"
          onClick={commit}
          disabled={busy}
          data-testid={`widget-id-save-${clientId}`}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 disabled:opacity-50"
          aria-label="Save widget_id"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          data-testid={`widget-id-cancel-${clientId}`}
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-[#94A3B8] hover:text-[#F8FAFC]"
          aria-label="Cancel"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <code
        data-testid={`widget-id-value-${clientId}`}
        title={current || "Not set"}
        className={`flex-1 truncate text-xs font-mono px-2 py-1 rounded-md ${
          current
            ? "bg-white/[0.04] text-[#F8FAFC]"
            : "bg-[#EC4899]/[0.08] text-[#EC4899]/90"
        }`}
      >
        {current || "Not set"}
      </code>
      <button
        type="button"
        onClick={startEdit}
        data-testid={`widget-id-edit-${clientId}`}
        className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#06B6D4]/40"
        aria-label="Edit widget_id"
      >
        <Pencil size={12} />
      </button>
    </div>
  );
}

function InviteHistoryDrawer({ open, onOpenChange, client, invites, loading, onCopy, onRegenerate, regenBusy }) {
  const businessName = client?.business_name || "client";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="invite-history-drawer"
        className="w-full sm:max-w-lg bg-[#0A1628] text-[#F8FAFC] border-l border-white/10 p-0 overflow-y-auto"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5 text-left">
          <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
            Invite history
          </div>
          <SheetTitle className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-[#F8FAFC] mt-1">
            {businessName}
          </SheetTitle>
          <SheetDescription className="text-xs text-[#94A3B8] font-mono">
            Every signup link issued for this client — newest first.
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 py-5 space-y-3">
          {loading && (
            <div data-testid="invite-history-loading" className="text-center py-10 text-[#94A3B8]">
              <Loader2 className="inline animate-spin mr-2" size={14} /> Loading history…
            </div>
          )}

          {!loading && invites.length === 0 && (
            <div data-testid="invite-history-empty" className="text-center py-10">
              <div className="text-sm text-[#F8FAFC] font-display">No invites issued yet</div>
              <div className="mt-2 text-xs text-[#94A3B8] max-w-xs mx-auto">
                Generate an invite link from the clients table to start the onboarding pipeline.
              </div>
            </div>
          )}

          {!loading && invites.map((inv, idx) => {
            const meta = INVITE_HISTORY_META[inv.status] || INVITE_HISTORY_META.pending;
            const isActive = inv.status === "pending" || inv.status === "clicked";
            const isExpired = inv.status === "expired";
            return (
              <div
                key={inv.token}
                data-testid={`invite-history-row-${idx}`}
                className="so-card rounded-xl p-4 border border-white/5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`pill ${meta.cls}`}
                        data-testid={`invite-history-status-${idx}`}
                      >
                        ● {meta.label}
                      </span>
                      {idx === 0 && (
                        <span className="text-[10px] uppercase tracking-widest font-mono text-[#06B6D4]">
                          Latest
                        </span>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <Clock size={11} className="shrink-0" />
                        <span className="font-mono uppercase tracking-wider text-[10px] w-16">Issued</span>
                        <span className="text-[#F8FAFC] font-mono">{fmtDateTime(inv.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <Clock size={11} className="shrink-0" />
                        <span className="font-mono uppercase tracking-wider text-[10px] w-16">Expires</span>
                        <span className="text-[#F8FAFC] font-mono">{fmtDateTime(inv.expires_at)}</span>
                      </div>
                      {inv.first_viewed_at && (
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <Clock size={11} className="shrink-0" />
                          <span className="font-mono uppercase tracking-wider text-[10px] w-16">Clicked</span>
                          <span className="text-[#F8FAFC] font-mono">{fmtDateTime(inv.first_viewed_at)}</span>
                        </div>
                      )}
                      {inv.used_at && (
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <Clock size={11} className="shrink-0" />
                          <span className="font-mono uppercase tracking-wider text-[10px] w-16">Signed up</span>
                          <span className="text-[#06B6D4] font-mono">{fmtDateTime(inv.used_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {isActive && (
                    <button
                      type="button"
                      onClick={() => onCopy(inv.invite_url)}
                      data-testid={`invite-history-copy-${idx}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider"
                    >
                      <Copy size={11} /> Copy link
                    </button>
                  )}
                  {isExpired && idx === 0 && (
                    <button
                      type="button"
                      onClick={() => onRegenerate()}
                      disabled={regenBusy}
                      data-testid={`invite-history-regenerate-${idx}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider disabled:opacity-50"
                    >
                      {regenBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                      Regenerate
                    </button>
                  )}
                  {isActive && (
                    <code
                      title={inv.invite_url}
                      className="flex-1 min-w-0 truncate text-[10px] font-mono px-2 py-1.5 rounded-md bg-white/[0.04] text-[#94A3B8]"
                    >
                      {inv.invite_url}
                    </code>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function slugifyBusinessName(name) {
  return (name || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")   // strip diacritics
    .replace(/[^a-z0-9]+/g, "-")       // non-alphanumeric → hyphen
    .replace(/^-+|-+$/g, "")           // trim leading/trailing hyphens
    .replace(/-{2,}/g, "-")            // collapse repeats
    .slice(0, 64);
}

function CreateClientDialog({ open, onOpenChange, onCreated }) {
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [widgetId, setWidgetId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Live widget_id availability check
  // status: idle | checking | available | taken | invalid | empty
  const [availability, setAvailability] = useState({ status: "idle" });

  const reset = () => {
    setBusinessName("");
    setContactName("");
    setEmail("");
    setWidgetId("");
    setSubmitting(false);
    setAvailability({ status: "idle" });
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // Debounced availability check whenever widgetId changes.
  useEffect(() => {
    if (!open) return;
    const slug = widgetId.trim().toLowerCase();
    if (!slug) {
      setAvailability({ status: "idle" });
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(slug)) {
      setAvailability({ status: "invalid" });
      return;
    }
    setAvailability({ status: "checking" });
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/widget-id/check", {
          params: { slug },
          headers: authHeaders(),
        });
        if (cancelled) return;
        if (!data?.valid) {
          setAvailability({ status: "invalid" });
        } else if (data.available) {
          setAvailability({ status: "available" });
        } else {
          setAvailability({ status: "taken" });
        }
      } catch {
        if (!cancelled) setAvailability({ status: "idle" });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [widgetId, open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const bn = businessName.trim();
    const cn = contactName.trim();
    const em = email.trim().toLowerCase();
    const wid = widgetId.trim().toLowerCase();
    if (!bn || !cn || !em || !wid) {
      toast.error("All fields are required");
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(wid)) {
      toast.error("Widget ID must be 3–64 chars, lowercase letters, digits and hyphens only");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/admin/clients",
        { business_name: bn, name: cn, contact_email: em, widget_id: wid },
        { headers: authHeaders() }
      );
      toast.success(`Client created: ${data.business_name}`);
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create client");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="create-client-dialog"
        className="bg-[#0A1628] text-[#F8FAFC] border border-white/10 max-w-md"
      >
        <DialogHeader className="text-left">
          <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
            New client
          </div>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-[#F8FAFC]">
            Create client manually
          </DialogTitle>
          <DialogDescription className="text-xs text-[#94A3B8]">
            Status defaults to <span className="font-mono text-[#F8FAFC]">pending</span>. You can generate an invite link from the clients table once created.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#94A3B8] mb-1.5 font-mono">
              Business name
            </label>
            <input
              data-testid="create-client-business-name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="SparkleClean Co."
              autoFocus
              className="so-input w-full rounded-lg px-3.5 py-2.5 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#94A3B8] mb-1.5 font-mono">
              Contact name
            </label>
            <input
              data-testid="create-client-contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Jane Doe"
              className="so-input w-full rounded-lg px-3.5 py-2.5 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#94A3B8] mb-1.5 font-mono">
              Email address
            </label>
            <input
              type="email"
              data-testid="create-client-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@sparkleclean.com.au"
              className="so-input w-full rounded-lg px-3.5 py-2.5 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-[#94A3B8] mb-1.5 font-mono">
              Widget ID
            </label>
            <div className="flex items-center gap-2">
              <input
                data-testid="create-client-widget-id"
                value={widgetId}
                onChange={(e) => setWidgetId(e.target.value)}
                placeholder="sparkleclean-co"
                className="so-input flex-1 min-w-0 rounded-lg px-3.5 py-2.5 text-sm font-mono"
                required
              />
              <button
                type="button"
                onClick={() => {
                  const slug = slugifyBusinessName(businessName);
                  if (!slug || slug.length < 3) {
                    toast.error("Add a business name first (at least 3 usable characters).");
                    return;
                  }
                  setWidgetId(slug);
                }}
                data-testid="create-client-generate-widget-id"
                title="Generate from business name"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider"
              >
                <Wand2 size={12} /> Generate
              </button>
            </div>
            <div className="mt-1.5 flex items-center gap-2 min-h-[18px]">
              {availability.status === "checking" && (
                <span
                  data-testid="widget-id-status-checking"
                  className="inline-flex items-center gap-1.5 text-[11px] text-[#94A3B8] font-mono"
                >
                  <Loader2 size={11} className="animate-spin" /> Checking availability…
                </span>
              )}
              {availability.status === "available" && (
                <span
                  data-testid="widget-id-status-available"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#06B6D4]/40 bg-[#06B6D4]/10 text-[#06B6D4] px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"
                >
                  <Check size={11} /> Available
                </span>
              )}
              {availability.status === "taken" && (
                <span
                  data-testid="widget-id-status-taken"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#EC4899]/40 bg-[#EC4899]/10 text-[#EC4899] px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"
                >
                  <AlertTriangle size={11} /> Already taken — please edit
                </span>
              )}
              {availability.status === "invalid" && (
                <span
                  data-testid="widget-id-status-invalid"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#EC4899]/40 bg-[#EC4899]/10 text-[#EC4899] px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider"
                >
                  <AlertTriangle size={11} /> Invalid format
                </span>
              )}
              {(availability.status === "idle" || availability.status === "empty") && (
                <p className="text-[11px] text-[#94A3B8] font-mono">
                  Lowercase letters, digits, and hyphens only. You can edit the generated value.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              data-testid="create-client-cancel"
              className="btn-ghost rounded-full px-4 py-2 text-xs uppercase tracking-widest font-mono"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || availability.status === "taken" || availability.status === "invalid" || availability.status === "checking"}
              data-testid="create-client-submit"
              className="btn-pink inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs uppercase tracking-widest font-mono disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
              Create client
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EmailPreviewDialog({ open, onOpenChange }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("welcome");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // Load template list once on open
  useEffect(() => {
    if (!open || templates.length) return;
    setLoadingList(true);
    api.get("/admin/emails/templates", { headers: authHeaders() })
      .then(({ data }) => setTemplates(data?.templates || []))
      .catch(() => toast.error("Could not load templates"))
      .finally(() => setLoadingList(false));
  }, [open, templates.length]);

  // Fetch the preview whenever the selected template changes (while open)
  useEffect(() => {
    if (!open || !selected) return;
    let cancelled = false;
    setLoading(true);
    setHtml("");
    api.post("/admin/emails/preview", { template: selected }, { headers: authHeaders() })
      .then(({ data }) => { if (!cancelled) setHtml(data?.html || ""); })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || "Could not render preview");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, selected]);

  const humanize = (name) =>
    name
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-preview-dialog"
        className="bg-[#0A1628] text-[#F8FAFC] border border-white/10 max-w-4xl p-0 overflow-hidden gap-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 text-left border-b border-white/5">
          <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
            Email templates
          </div>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-[#F8FAFC]">
            Preview transactional emails
          </DialogTitle>
          <DialogDescription className="text-xs text-[#94A3B8]">
            Renders with realistic sample data. Nothing is sent.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <label className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono">
            Template
          </label>
          <select
            data-testid="email-preview-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={loadingList}
            className="so-input rounded-lg px-3 py-2 text-sm font-mono min-w-[240px] disabled:opacity-60"
          >
            {templates.map((t) => (
              <option key={t.name} value={t.name}>
                {humanize(t.name)}
              </option>
            ))}
            {templates.length === 0 && <option value="welcome">welcome</option>}
          </select>
          {loading && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#94A3B8] font-mono">
              <Loader2 size={11} className="animate-spin" /> Rendering…
            </span>
          )}
        </div>

        <div className="bg-[#F1F5F9] p-4" style={{ minHeight: 520 }}>
          {loading && !html ? (
            <div className="h-[520px] flex items-center justify-center text-[#0A1628] text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading preview…
            </div>
          ) : (
            <iframe
              data-testid="email-preview-iframe"
              title={`Email preview: ${selected}`}
              srcDoc={html}
              sandbox=""
              className="w-full rounded-lg border border-[#E2E8F0] bg-white"
              style={{ height: 640, display: "block" }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [actingId, setActingId] = useState(null);
  // Local map of clientId → {invite_url, expires_at} for the most recent invite generated this session
  const [inviteCache, setInviteCache] = useState({});
  // Invite history drawer state
  const [historyClientId, setHistoryClientId] = useState(null);
  const [historyInvites, setHistoryInvites] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Create client dialog
  const [createOpen, setCreateOpen] = useState(false);
  // Delete client confirmation
  const [pendingDelete, setPendingDelete] = useState(null); // { id, business_name, status } | null
  const [deleting, setDeleting] = useState(false);
  // Email preview dialog
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  const fmtAud = (n) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency", currency: "AUD", maximumFractionDigits: 0,
    }).format(n || 0);

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin", { replace: true }); return; }
    refresh();
    // eslint-disable-next-line
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/stats", { headers: authHeaders() }),
        api.get("/admin/clients", { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setClients(c.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin", { replace: true });
        return;
      }
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    setAdminToken(null);
    navigate("/admin", { replace: true });
  };

  const onOverride = async (id, currentStatus) => {
    setActingId(id);
    const newStatus = currentStatus === "inactive" ? "active" : "inactive";
    try {
      const { data } = await api.patch(
        `/admin/clients/${id}/override`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      setClients((prev) => prev.map((c) => (c.id === id ? { ...c, status: data.status } : c)));
      toast.success(`Client manually ${data.status}`);
      try {
        const s = await api.get("/admin/stats", { headers: authHeaders() });
        setStats(s.data);
      } catch {
        // Stats refresh is best-effort; the override itself already succeeded.
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setActingId(null);
    }
  };

  const onRequestDelete = (client) => {
    // Guard client-side too, so the confirmation only opens for eligible statuses
    // (still enforced server-side).
    const status = (client?.status || "").toLowerCase();
    if (status !== "pending" && status !== "inactive") {
      toast.error("Active clients must be cancelled in Stripe before deleting.");
      return;
    }
    setPendingDelete({
      id: client.id,
      business_name: client.business_name,
      status: client.status,
    });
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/clients/${pendingDelete.id}`, { headers: authHeaders() });
      setClients((prev) => prev.filter((c) => c.id !== pendingDelete.id));
      toast.success(`Deleted ${pendingDelete.business_name}`);
      setPendingDelete(null);
      // Best-effort stats refresh
      try {
        const s = await api.get("/admin/stats", { headers: authHeaders() });
        setStats(s.data);
      } catch { /* stats refresh is best-effort */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const onSaveWidgetId = async (id, newWidgetId) => {
    const trimmed = (newWidgetId || "").trim().toLowerCase();
    if (!trimmed) {
      toast.error("widget_id cannot be empty");
      return false;
    }
    setActingId(id);
    try {
      const { data } = await api.patch(
        `/admin/clients/${id}/widget-id`,
        { widget_id: trimmed },
        { headers: authHeaders() }
      );
      setClients((prev) =>
        prev.map((c) => (c.id === id ? { ...c, widget_id: data.widget_id } : c))
      );
      toast.success(`Widget linked: ${data.widget_id}`);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update widget_id");
      return false;
    } finally {
      setActingId(null);
    }
  };

  const onSaveTrialEnd = async (id, dateStr) => {
    if (!dateStr) {
      toast.error("Pick a valid date");
      return false;
    }
    setActingId(id);
    try {
      const { data } = await api.patch(
        `/admin/clients/${id}/trial-end`,
        { trial_end_date: dateStr },
        { headers: authHeaders() }
      );
      setClients((prev) =>
        prev.map((c) => (c.id === id ? { ...c, trial_end_date: data.trial_end_date } : c))
      );
      toast.success("Trial end date updated");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update trial end date");
      return false;
    } finally {
      setActingId(null);
    }
  };

  const onGenerateInvite = async (id) => {
    if (!id) return;
    setActingId(id);
    try {
      const { data } = await api.post(
        `/admin/clients/${id}/invite`,
        {},
        { headers: authHeaders() }
      );
      setInviteCache((prev) => ({
        ...prev,
        [id]: { invite_url: data.invite_url, expires_at: data.expires_at },
      }));
      setClients((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, invite_status: "invited", invite_expires_at: data.expires_at }
            : c
        )
      );
      try {
        await navigator.clipboard.writeText(data.invite_url);
        toast.success("Invite link copied to clipboard");
      } catch {
        toast.success("Invite generated");
      }
      // If the history drawer is open for this client, refresh it.
      if (historyClientId === id) {
        await loadInviteHistory(id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate invite");
    } finally {
      setActingId(null);
    }
  };

  const loadInviteHistory = async (id) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(
        `/admin/clients/${id}/invites`,
        { headers: authHeaders() }
      );
      setHistoryInvites(data?.invites || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load invite history");
      setHistoryInvites([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const onViewHistory = async (id) => {
    setHistoryClientId(id);
    setHistoryInvites([]);
    await loadInviteHistory(id);
  };

  const onCopyHistoryLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const onRegenerateFromHistory = async () => {
    if (!historyClientId) return;
    await onGenerateInvite(historyClientId);
  };

  const filtered = useMemo(() => {
    let arr = clients;
    if (filter !== "all") arr = arr.filter((c) => c.status === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((c) =>
        (c.business_name || "").toLowerCase().includes(q) ||
        (c.contact_email || "").toLowerCase().includes(q) ||
        (c.name || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [clients, search, filter]);

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F8FAFC]">
      <header className="border-b border-white/5 bg-[#0A1628]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/genquotes-logo.png?v=3" alt="GenQuotes" className="h-9 w-auto" style={{maxHeight:36}}/>
            <span className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono border-l border-white/10 pl-3 ml-1">
              Admin Console
            </span>
          </Link>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-8">
          <div>
            <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">Overview</div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              All clients
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEmailPreviewOpen(true)}
              data-testid="admin-preview-emails-btn"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
            >
              <Mail size={14} /> Preview emails
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              data-testid="admin-create-client-btn"
              className="btn-pink inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
            >
              <UserPlus size={14} /> Create client
            </button>
            <button
              onClick={refresh}
              data-testid="admin-refresh-btn"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="admin-stats-grid">
          <StatCard icon={Users} label="Total" value={stats ? stats.total_clients : "—"} accent="All accounts" />
          <StatCard icon={Hourglass} label="In trial" value={stats ? stats.trial_clients : "—"} accent="28-day free" />
          <StatCard icon={Activity} label="Active" value={stats ? stats.active_clients : "—"} accent="Paying" />
          <StatCard icon={AlertTriangle} label="Payment failed" value={stats ? stats.payment_failed_clients : "—"} accent="Needs attention" tone="pink" />
          <StatCard icon={DollarSign} label="Est. MRR" value={stats ? fmtAud(stats.estimated_mrr_aud) : "—"} accent="AUD · monthly" />
        </div>

        <div className="mt-8 sm:mt-10 flex items-center gap-3 flex-wrap">
          <div className="relative w-full sm:flex-1 sm:max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by business, name or email…"
              className="so-input w-full rounded-full pl-9 pr-4 py-2.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 w-full sm:w-auto pb-1 sm:pb-0">
            {[
              ["all","All"],
              ["trial","Trial"],
              ["active","Active"],
              ["payment_failed","Payment failed"],
              ["inactive","Inactive"],
            ].map(([k,l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`filter-${k}`}
                className={`shrink-0 text-[11px] uppercase tracking-widest font-mono px-3 py-1.5 rounded-full border transition-colors ${
                  filter === k
                    ? "bg-[#06B6D4] text-[#0A1628] border-[#06B6D4]"
                    : "border-white/10 text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#06B6D4]/40"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="text-xs text-[#94A3B8] font-mono sm:ml-auto">
            {filtered.length} of {clients.length}
          </div>
        </div>

        <div className="mt-5 so-card rounded-2xl overflow-hidden">
          {/* Desktop header row */}
          <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono border-b border-white/5 gap-3">
            <div className="col-span-3">Client</div>
            <div className="col-span-3">Widget ID</div>
            <div className="col-span-2">Invite</div>
            <div className="col-span-2">Trial / Next billing</div>
            <div className="col-span-1 text-right">Quotes</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading && clients.length === 0 && (
            <div className="px-6 py-10 text-center text-[#94A3B8]" data-testid="admin-loading">
              <Loader2 className="inline animate-spin mr-2" size={14} /> Loading clients…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="px-6 py-16 text-center" data-testid="admin-empty">
              <div className="text-[#F8FAFC] font-display text-lg">No clients yet</div>
              <div className="mt-2 text-sm text-[#94A3B8] max-w-md mx-auto">
                Clients will appear here as soon as they sign up for a free trial.
              </div>
            </div>
          )}

          {filtered.map((c) => {
            const isFailed = c.status === "payment_failed";
            return (
              <div
                key={c.id}
                data-testid={`client-row-${c.id}`}
                className={`px-4 sm:px-6 py-4 border-b border-white/5 transition-colors ${
                  isFailed ? "bg-[#EC4899]/[0.06]" : "hover:bg-white/[0.02]"
                } md:grid md:grid-cols-12 md:items-center md:gap-3`}
              >
                {/* Mobile: stacked card. Desktop: grid columns */}
                <div className="md:col-span-3 min-w-0 flex items-start justify-between gap-3 md:block">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{c.business_name}</div>
                    <div className="text-xs text-[#94A3B8] truncate">
                      {c.name ? `${c.name} · ` : ""}{c.contact_email}
                    </div>
                    <div className="hidden md:block mt-2"><StatusPill status={c.status} /></div>
                  </div>
                  <div className="md:hidden shrink-0"><StatusPill status={c.status} /></div>
                </div>
                <div className="mt-3 md:mt-0 md:col-span-3 min-w-0">
                  <div className="md:hidden text-[10px] uppercase tracking-widest text-[#94A3B8] font-mono mb-1.5">Widget ID</div>
                  <WidgetIdCell
                    clientId={c.id}
                    current={c.widget_id}
                    busy={actingId === c.id}
                    onSave={onSaveWidgetId}
                  />
                </div>
                <div className="mt-3 md:mt-0 md:col-span-2 min-w-0 overflow-hidden">
                  <div className="md:hidden text-[10px] uppercase tracking-widest text-[#94A3B8] font-mono mb-1.5">Invite</div>
                  <InviteCell
                    clientId={c.id}
                    invite_status={c.invite_status}
                    busy={actingId === c.id}
                    onGenerate={onGenerateInvite}
                    onViewHistory={onViewHistory}
                    lastInvite={inviteCache[c.id]}
                  />
                </div>
                <div className="mt-3 md:mt-0 md:col-span-2 flex items-baseline justify-between md:block gap-3">
                  <div className="min-w-0">
                    {c.status === "trial" ? (
                      <>
                        <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono">Trial ends</div>
                        <div className="mt-0.5">
                          <EditableTrialEndCell
                            clientId={c.id}
                            value={c.trial_end_date}
                            busy={actingId === c.id}
                            onSave={onSaveTrialEnd}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#94A3B8] font-mono">Next billing</div>
                        <div className="text-sm font-mono">{fmtDate(c.next_billing_date)}</div>
                        <div className="mt-2 text-[10px] uppercase tracking-widest text-[#94A3B8] font-mono">Trial ends</div>
                        <div className="mt-0.5">
                          <EditableTrialEndCell
                            clientId={c.id}
                            value={c.trial_end_date}
                            busy={actingId === c.id}
                            onSave={onSaveTrialEnd}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="md:hidden text-right">
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-mono">Quotes</div>
                    <div className="text-sm font-mono">{(c.monthly_quote_volume || 0).toLocaleString()}</div>
                  </div>
                </div>
                <div className="hidden md:block md:col-span-1 text-right text-sm font-mono">
                  {(c.monthly_quote_volume || 0).toLocaleString()}
                </div>
                <div className="mt-4 md:mt-0 md:col-span-1 md:text-right">
                  <div className="flex items-center justify-start md:justify-end gap-1.5">
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onOverride(c.id, c.status)}
                            disabled={actingId === c.id}
                            data-testid={`override-client-${c.id}`}
                            aria-label={c.status === "inactive" ? "Activate client" : "Deactivate client"}
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all ${
                              c.status === "inactive"
                                ? "border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10"
                                : "border-[#EC4899]/40 text-[#EC4899] hover:bg-[#EC4899]/10"
                            } disabled:opacity-50`}
                          >
                            {actingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {c.status === "inactive" ? "Activate" : "Deactivate"}
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onRequestDelete(c)}
                            disabled={actingId === c.id}
                            data-testid={`delete-client-${c.id}`}
                            aria-label={`Delete ${c.business_name}`}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#EC4899]/40 text-[#EC4899] hover:bg-[#EC4899]/10 disabled:opacity-50"
                          >
                            <Trash2 size={14} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Delete</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <InviteHistoryDrawer
        open={!!historyClientId}
        onOpenChange={(v) => { if (!v) setHistoryClientId(null); }}
        client={clients.find((c) => c.id === historyClientId)}
        invites={historyInvites}
        loading={historyLoading}
        onCopy={onCopyHistoryLink}
        onRegenerate={onRegenerateFromHistory}
        regenBusy={actingId === historyClientId}
      />

      <CreateClientDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(newClient) => {
          setClients((prev) => [
            {
              ...newClient,
              monthly_quote_volume: 0,
              invite_status: "not_invited",
            },
            ...prev,
          ]);
          // Refresh stats so Total ticks up
          api.get("/admin/stats", { headers: authHeaders() })
            .then(({ data }) => setStats(data))
            .catch(() => { /* stats refresh is best-effort */ });
        }}
      />

      <EmailPreviewDialog
        open={emailPreviewOpen}
        onOpenChange={setEmailPreviewOpen}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v && !deleting) setPendingDelete(null); }}
      >
        <AlertDialogContent
          data-testid="delete-client-dialog"
          className="bg-[#0A1628] text-[#F8FAFC] border border-white/10 max-w-md"
        >
          <AlertDialogHeader className="text-left">
            <div className="uppercase text-[11px] tracking-widest text-[#EC4899] font-mono">
              Delete client
            </div>
            <AlertDialogTitle className="font-display text-xl font-extrabold tracking-tight text-[#F8FAFC]">
              Are you sure you want to delete {pendingDelete?.business_name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#94A3B8]">
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-2">
            <AlertDialogCancel
              data-testid="delete-client-cancel"
              disabled={deleting}
              className="btn-ghost rounded-full px-4 py-2 text-xs uppercase tracking-widest font-mono border border-white/10 bg-transparent text-[#F8FAFC] hover:bg-white/5 hover:text-[#F8FAFC]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-client-confirm"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); onConfirmDelete(); }}
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs uppercase tracking-widest font-mono bg-[#EC4899] text-[#F8FAFC] hover:bg-[#EC4899]/90 disabled:opacity-60"
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
